<template>
  <div id="app">
    <!-- 导航栏 -->
    <nav class="navbar bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <router-link to="/" class="flex items-center space-x-2">
            <div class="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <span class="text-xl font-bold text-white">CyberGame Hub</span>
          </router-link>

          <!-- 桌面导航 -->
          <div class="hidden md:flex items-center space-x-6">
            <router-link
              to="/"
              class="nav-link"
              :class="{ 'active': $route.name === 'Home' }"
            >
              Home
            </router-link>
            <router-link
              to="/games"
              class="nav-link"
              :class="{ 'active': $route.name === 'Games' }"
            >
              Games
            </router-link>
            <router-link
              to="/news"
              class="nav-link"
              :class="{ 'active': $route.name === 'News' }"
            >
              News
            </router-link>
            <router-link
              to="/reviews"
              class="nav-link"
              :class="{ 'active': $route.name === 'Reviews' }"
            >
              Reviews
            </router-link>
            <router-link
              to="/guides"
              class="nav-link"
              :class="{ 'active': $route.name === 'Guides' }"
            >
              Guides
            </router-link>
            <router-link
              to="/about"
              class="nav-link"
              :class="{ 'active': $route.name === 'About' }"
            >
              About
            </router-link>
          </div>

          <!-- 用户菜单 -->
          <div class="flex items-center space-x-4">
            <router-link
              to="/user"
              class="text-gray-300 hover:text-white transition-colors duration-200"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </router-link>

            <!-- 移动端菜单按钮 -->
            <button
              @click="toggleMobileMenu"
              class="md:hidden text-gray-300 hover:text-white transition-colors duration-200"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- 移动端菜单 -->
        <div
          v-show="mobileMenuOpen"
          class="md:hidden border-t border-gray-800 py-4"
        >
          <div class="flex flex-col space-y-4">
            <router-link
              to="/"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'Home' }"
              @click="closeMobileMenu"
            >
              Home
            </router-link>
            <router-link
              to="/games"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'Games' }"
              @click="closeMobileMenu"
            >
              Games
            </router-link>
            <router-link
              to="/news"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'News' }"
              @click="closeMobileMenu"
            >
              News
            </router-link>
            <router-link
              to="/reviews"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'Reviews' }"
              @click="closeMobileMenu"
            >
              Reviews
            </router-link>
            <router-link
              to="/guides"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'Guides' }"
              @click="closeMobileMenu"
            >
              Guides
            </router-link>
            <router-link
              to="/about"
              class="mobile-nav-link"
              :class="{ 'active': $route.name === 'About' }"
              @click="closeMobileMenu"
            >
              About
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- 页脚 -->
    <footer class="footer bg-gray-900 border-t border-gray-800">
      <div class="container mx-auto px-4 py-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- 品牌信息 -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-2 mb-4">
              <div class="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span class="text-xl font-bold text-white">CyberGame Hub</span>
            </div>
            <p class="text-gray-400 mb-4">
              CyberGame Hub is the premier browser gaming platform featuring 500+ carefully curated games across 15+ genres. Join our community of 2.5M+ active players worldwide. Experience instant gaming without downloads, read expert reviews, access comprehensive guides, and participate in weekly tournaments. All games are free and optimized for seamless browser play.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-gray-400 hover:text-white transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" class="text-gray-400 hover:text-white transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                </svg>
              </a>
              <a href="#" class="text-gray-400 hover:text-white transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- 快速链接 -->
          <div>
            <h3 class="text-white font-semibold mb-4">Quick Links</h3>
            <ul class="space-y-2">
              <li>
                <router-link to="/games" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Games
                </router-link>
              </li>
              <li>
                <router-link to="/news" class="text-gray-400 hover:text-white transition-colors duration-200">
                  News
                </router-link>
              </li>
              <li>
                <router-link to="/reviews" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Reviews
                </router-link>
              </li>
              <li>
                <router-link to="/guides" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Guides
                </router-link>
              </li>
              <li>
                <router-link to="/about" class="text-gray-400 hover:text-white transition-colors duration-200">
                  About
                </router-link>
              </li>
            </ul>
          </div>

          <!-- 法律信息 -->
          <div>
            <h3 class="text-white font-semibold mb-4">Legal</h3>
            <ul class="space-y-2">
              <li>
                <router-link to="/privacy" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Privacy Policy
                </router-link>
              </li>
              <li>
                <router-link to="/terms" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Terms of Service
                </router-link>
              </li>
              <li>
                <a href="mailto:zhangshuaidoc@163.com" class="text-gray-400 hover:text-white transition-colors duration-200">
                  Contact Us
                </a>
              </li>
              <li>
                <button 
                  @click="showReportModal" 
                  class="text-gray-400 hover:text-white transition-colors duration-200 text-left"
                >
                  Report Content
                </button>
              </li>
              <li class="text-gray-400">
                Hours: 9:00-18:00
              </li>
            </ul>
          </div>
        </div>

        <!-- 版权信息 -->
        <div class="border-t border-gray-800 mt-8 pt-8 text-center">
          <p class="text-gray-400">
            © 2024-2026 CyberGame Hub. All rights reserved.
          </p>
        </div>
      </div>
    </footer>

    <!-- Cookie Consent Banner -->
    <CookieConsent />

    <!-- Report Content Modal -->
    <ReportContent ref="reportModal" />
  </div>
</template>

<script>
import { ref } from 'vue'
import CookieConsent from './components/CookieConsent.vue'
import ReportContent from './components/ReportContent.vue'

export default {
  name: 'App',
  components: {
    CookieConsent,
    ReportContent
  },
  setup() {
    const mobileMenuOpen = ref(false)
    const reportModal = ref(null)

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
    }

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false
    }

    const showReportModal = () => {
      if (reportModal.value) {
        reportModal.value.show()
      }
    }

    return {
      mobileMenuOpen,
      reportModal,
      toggleMobileMenu,
      closeMobileMenu,
      showReportModal
    }
  }
}
</script>

<style>
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  min-height: 100vh;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

/* 导航链接样式 */
.nav-link {
  @apply text-gray-300 hover:text-white transition-colors duration-200 font-medium;
}

.nav-link.active {
  @apply text-blue-400;
}

.mobile-nav-link {
  @apply text-gray-300 hover:text-white transition-colors duration-200 font-medium block py-2;
}

.mobile-nav-link.active {
  @apply text-blue-400;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #1f2937;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style> 